import _ from "lodash"
import { useNavigate } from "react-router-dom"
import {
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
  cloneElement,
  isValidElement,
} from "react"
import { Menubar } from "primereact/menubar"
import { UserContext } from "../context/userContext"
import { FirebaseContext } from "../firebase"
import AdditionalServicesOverlays from "../components/AdditionalServicesOverlays"
import { LoaderProgressBar } from "../components/Loaders"
import { useQuickActionsMenu } from "../nav/NavMain/QuickActionsMenu"
import useAdditionalServices from "../hooks/useAdditionalServices"
import { mapMenuItemsForMenubar } from "../helpers/menuBuilder"
import { LogError, LogInfo } from "../helpers/logger"
import useMainMenu from "../hooks/useMainMenu"
import useTemplateAvailability from "../hooks/useTemplateAvailability"

import styles from "../styles/Nav-Main.module.css"

const NAV_OVERFLOW_START_PX = 1120
const NAV_PRE_OVERFLOW_BUFFER_PX = 30
const NAV_FIRST_OVERFLOW_OFFSET_PX = 80
const NAV_OVERFLOW_STEP_WIDTH_PX = 130

export default function NavMain() {
  /* Core refs, state, and context used across nav rendering and effects. */
  const navRootRef = useRef(null)
  // Seed with viewport width so first render has a stable overflow baseline.
  const [menuContainerWidth, setMenuContainerWidth] = useState(
    typeof window === "undefined" ? 1440 : window.innerWidth,
  )

  // Quick action toggles for controlling the visibility and state of service overlays.
  const [quickActionToggles, setQuickActionToggles] = useState({
    documentsEnabled: false,
    documentsOpen: false,
    telerecovery: false,
    housingEnabled: false,
    housingOpen: false,
  })
  const userCtx = useContext(UserContext)
  const firebase = useContext(FirebaseContext)
  const navigate = useNavigate()

  // Pull the main menu items, loading state, and error from the shared hook.
  const {
    menuItems: menu,
    loading: loadingMenu,
    error: menuError,
    resolveMenuPath,
  } = useMainMenu({
    excludeLabels: [
      "Application Management",
      "Organization Management",
      "Enterprise Management",
    ],
  })

  // Keep template availability checks in sync with menu/ux refresh events.
  const templatesReloadDeps = useMemo(
    () => [userCtx.session.menu, userCtx.session.ux],
    [userCtx.session.menu, userCtx.session.ux],
  )

  const { customTemplates, loadingTemplates, isTemplateAvailable } =
    useTemplateAvailability({
      // Preserve previous refresh behavior used in NavMain.
      reloadDeps: templatesReloadDeps,
    })

  // Side effects.
  useEffect(() => {
    if (menuError) {
      LogError(`Encountered error: ${menuError}`)
    }
  }, [menuError])

  useEffect(() => {
    // Mirror microservice flags used by services/quick-actions visibility.
    const microservicesRef = firebase.db
      .collection("enterprise-config")
      .doc(userCtx.session.user.enterprise_id)
      .collection("organizations")
      .doc(userCtx.session.user.organization_id)
      .collection("microservices")

    const unsubscribe = microservicesRef.onSnapshot(
      (snapshot) => {
        if (!snapshot.empty) {
          const microservicesData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))

          const updateToggles = {
            telerecovery: !!microservicesData.find(
              (service) => service.id === "1",
            )?.enable,
            documentsEnabled: !!microservicesData.find(
              (service) => service.id === "8",
            )?.enable,
            housingEnabled: !!microservicesData.find(
              (service) => service.id === "7",
            )?.enable,
          }

          setQuickActionToggles((prev) => ({ ...prev, ...updateToggles }))
        }
      },
      (error) => {
        LogInfo("Error listening to microservices collection:", error)
      },
    )

    return () => unsubscribe()
  }, [
    firebase.db,
    userCtx.session.user.enterprise_id,
    userCtx.session.user.organization_id,
  ])

  // When shared context emits a document-open signal, flip the Documents toggle
  // so the centralized overlay renderer mounts the Documents experience.
  useEffect(() => {
    if (userCtx.triggerDocumentRequestShow !== undefined) {
      setQuickActionToggles((prev) => ({ ...prev, documentsOpen: true }))
    }
  }, [userCtx.triggerDocumentRequestShow])

  // When shared context emits a housing-open signal, open the Housing dialog,
  // then immediately clear the one-shot context flag to prevent repeated opens.
  useEffect(() => {
    if (userCtx.housing?.context?.showHousing) {
      setQuickActionToggles((prev) => ({ ...prev, housingOpen: true }))
      userCtx.housing.setHousing({
        ...userCtx.housing.context,
        showHousing: false,
      })
    }
  }, [userCtx.housing])

  useEffect(() => {
    // Track the rendered nav container width so More overflow logic
    // is based on actual available menu space.
    if (!navRootRef.current) {
      return undefined
    }

    const element = navRootRef.current
    const updateFromElement = () => {
      const width = element.getBoundingClientRect().width
      if (width > 0) {
        setMenuContainerWidth(width)
      }
    }

    updateFromElement()

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(() => {
        updateFromElement()
      })

      resizeObserver.observe(element)
      return () => resizeObserver.disconnect()
    }

    const onResize = () => updateFromElement()
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const renderMenuLink = (item, options) => {
    const hasSubmenu = Array.isArray(item.items) && item.items.length > 0

    // Keep PrimeReact's native parent element so submenu ARIA state stays intact.
    if (hasSubmenu) {
      if (!item.icon || !isValidElement(options.element)) {
        return options.element
      }

      // Add configured icon without replacing native submenu trigger semantics.
      return cloneElement(
        options.element,
        {},
        <>
          <span className="material-icons p-menuitem-icon">{item.icon}</span>
          {options.element.props.children}
        </>,
      )
    }

    return (
      <a
        href={item.url || "#"}
        role="menuitem"
        className={options.className}
        target={item.target}
        aria-haspopup={hasSubmenu}
        onClick={options.onClick}
        onKeyDown={options.onKeyDown}
        title={item.label}
      >
        {item.icon && (
          <span className="material-icons p-menuitem-icon">{item.icon}</span>
        )}
        <span className={options.labelClassName}>{item.label}</span>
        {hasSubmenu && (
          <span className={options.submenuIconClassName} aria-hidden="true" />
        )}
      </a>
    )
  }

  const mapNavMenuItems = (items = []) => {
    // Shared mapper for configured menu rows -> Menubar-ready model entries.
    return mapMenuItemsForMenubar(items, {
      template: renderMenuLink,
      resolvePath: resolveMenuPath,
      navigateTo: (path) => navigate(path),
      resolveCommand: (row) => {
        if (row.label === "My Availability") {
          return () => {
            userCtx.setToggleAppointments({
              toggle: true,
              page: "availability",
            })
          }
        }

        return undefined
      },
    })
  }

  const getCustomTemplateItems = (template_category) => {
    const customTmpList = _.filter(customTemplates, { template_category })

    return _.compact(
      _.map(customTmpList, (row) => {
        if (!isTemplateAvailable({ template_id: row.template_id })) {
          return null
        }

        if (template_category === "activity_custom") {
          return {
            label: row.template_title,
            icon: "book",
            command: () => {
              navigate(`/activity-log/${row.template_id}/`)
            },
            template: renderMenuLink,
          }
        }

        return null
      }),
    )
  }

  // Base model from configured menu + template-available children + custom activity logs.
  const menuModel = _.compact(
    _.map(menu, (row) => {
      const secondLevelItems = mapNavMenuItems(
        _.filter(row.items || [], (child) =>
          isTemplateAvailable({ template_title: child.label }),
        ),
      )

      const customItems =
        row.label === "Activity Logs"
          ? getCustomTemplateItems("activity_custom")
          : []

      return {
        label: row.label,
        icon: row.icon,
        template: renderMenuLink,
        items: [...secondLevelItems, ...customItems],
      }
    }),
  )

  // Shared core hook injects/merges Services.
  const { menuModelWithAdditionalServices } = useAdditionalServices({
    staffRole: userCtx.session.user.staff_role,
    toggles: quickActionToggles,
    navigate,
    menuModel,
    onOpenDocuments: () =>
      setQuickActionToggles((prev) => ({ ...prev, documentsOpen: true })),
    onOpenHousing: () =>
      setQuickActionToggles((prev) => ({ ...prev, housingOpen: true })),
    menuItemTemplate: renderMenuLink,
  })

  const { quickActionsMenuItem } = useQuickActionsMenu({
    staffRole: userCtx.session.user.staff_role,
    navigate,
    menuItemTemplate: renderMenuLink,
  })

  const hasQuickActionsItems =
    Array.isArray(quickActionsMenuItem?.items) &&
    quickActionsMenuItem.items.length > 0

  // Append Quick Actions only when it has visible items and is not already present.
  const menuModelWithQuickActions = useMemo(() => {
    if (!hasQuickActionsItems) {
      return menuModelWithAdditionalServices
    }

    const hasQuickActions = _.some(
      menuModelWithAdditionalServices,
      (row) => row.label === quickActionsMenuItem.label,
    )

    if (hasQuickActions) {
      return menuModelWithAdditionalServices
    }

    return [...menuModelWithAdditionalServices, quickActionsMenuItem]
  }, [
    hasQuickActionsItems,
    menuModelWithAdditionalServices,
    quickActionsMenuItem,
  ])

  // Final top-level model with responsive overflow packing into a More group.
  const fullMenuModel = useMemo(() => {
    const quickActionsTopLevel = _.find(
      menuModelWithQuickActions,
      (row) => row.label === quickActionsMenuItem.label,
    )
    const overflowEligibleItems = _.filter(
      menuModelWithQuickActions,
      (row) => row.label !== quickActionsMenuItem.label,
    )

    if (
      menuContainerWidth >
      NAV_OVERFLOW_START_PX + NAV_PRE_OVERFLOW_BUFFER_PX
    ) {
      return quickActionsTopLevel
        ? [...overflowEligibleItems, quickActionsTopLevel]
        : overflowEligibleItems
    }

    // Delay the first overflow slightly, then move items into More on a steady cadence.
    const allTopLevelCount = overflowEligibleItems.length
    const firstOverflowVisibleCount = allTopLevelCount - 1
    const shrinkPx =
      NAV_OVERFLOW_START_PX + NAV_PRE_OVERFLOW_BUFFER_PX - menuContainerWidth
    if (shrinkPx < NAV_FIRST_OVERFLOW_OFFSET_PX) {
      return quickActionsTopLevel
        ? [...overflowEligibleItems, quickActionsTopLevel]
        : overflowEligibleItems
    }

    const additionalOverflowSteps = Math.floor(
      (shrinkPx - NAV_FIRST_OVERFLOW_OFFSET_PX) / NAV_OVERFLOW_STEP_WIDTH_PX,
    )
    const keepVisibleCount = Math.max(
      1,
      firstOverflowVisibleCount - additionalOverflowSteps,
    )

    const visibleTopLevel = overflowEligibleItems.slice(0, keepVisibleCount)
    const overflowSource = overflowEligibleItems.slice(keepVisibleCount)

    if (!overflowSource.length) {
      return quickActionsTopLevel
        ? [...overflowEligibleItems, quickActionsTopLevel]
        : overflowEligibleItems
    }

    const flattenedOverflowItems = _.flatMap(overflowSource, (group, idx) => {
      const headerItem = {
        label: group.label,
        disabled: true,
      }

      const groupItems = Array.isArray(group.items) ? group.items : []
      const needsSeparator = idx < overflowSource.length - 1

      if (needsSeparator) {
        return [headerItem, ...groupItems, { separator: true }]
      }

      return [headerItem, ...groupItems]
    })

    const modelWithOverflow = [
      ...visibleTopLevel,
      {
        label: "More",
        className: "nav-more-item",
        items: flattenedOverflowItems,
      },
    ]

    return quickActionsTopLevel
      ? [...modelWithOverflow, quickActionsTopLevel]
      : modelWithOverflow
  }, [
    menuContainerWidth,
    menuModelWithQuickActions,
    quickActionsMenuItem.label,
  ])

  // Keep loading behavior aligned across menu and template sources.
  if (loadingMenu || loadingTemplates)
    return (
      <nav ref={navRootRef} className={`nav-main ${styles.navMain}`}>
        <LoaderProgressBar />
      </nav>
    )

  return (
    <nav ref={navRootRef} className={`nav-main ${styles.navMain}`}>
      <Menubar
        className={`main-top-menubar ${styles.mainTopMenubar}`}
        model={fullMenuModel}
        autoDisplay={false}
      />

      <AdditionalServicesOverlays
        toggles={quickActionToggles}
        setToggles={setQuickActionToggles}
      />
    </nav>
  )
}
